<script setup>
import { onMounted, ref, watch, nextTick } from 'vue';
import Prism from 'prismjs';
// Try these themes (uncomment one):
// import 'prismjs/themes/prism.css'; // Default - clean and simple
import 'prismjs/themes/prism-solarizedlight.css'; // Soft colors, easy on eyes
// import 'prismjs/themes/prism-coy.css'; // Minimal and elegant
// import 'prismjs/themes/prism-okaidia.css'; // Dark theme (if you want contrast)
// import 'prismjs/themes/prism-twilight.css'; // Balanced colors
import 'prismjs/components/prism-json';
import 'prismjs/components/prism-javascript';

const props = defineProps({
  code: {
    type: String,
    required: true,
  },
});

const codeElement = ref(null);
const formattedCode = ref('');

function formatCode(code) {
  try {
    // Try to parse as JSON first
    const parsed = JSON.parse(code);
    return JSON.stringify(parsed, null, 2);
  } catch {
    // If not valid JSON, return as-is
    return code;
  }
}

function detectLanguage(code) {
  try {
    JSON.parse(code);
    return 'json';
  } catch {
    return 'javascript';
  }
}

function highlightCode() {
  formattedCode.value = formatCode(props.code);
  nextTick(() => {
    if (codeElement.value) {
      Prism.highlightElement(codeElement.value);
    }
  });
}

onMounted(() => {
  highlightCode();
});

// Re-render when code prop changes
watch(
  () => props.code,
  () => {
    highlightCode();
  }
);
</script>

<template>
  <div
    data-component="CsAiActivityCodePreview"
    class="code-preview-wrapper block h-fit w-full rounded-lg bg-brand-surface-secondary p-2 text-sm"
  >
    <pre class="!m-0 !bg-transparent"><code
        ref="codeElement"
        :class="`language-${detectLanguage(code)}`"
      >{{ formattedCode }}</code></pre>
  </div>
</template>

<style scoped>
.code-preview-wrapper :deep(pre) {
  margin: 0;
  padding: 0;
  background: transparent !important;
}

.code-preview-wrapper :deep(code) {
  background: transparent !important;
  font-size: 0.875rem;
  line-height: 1.5;
}

.code-preview-wrapper :deep(pre[class*='language-']) {
  background: transparent !important;
  margin: 0;
  padding: 0;
}
</style>
